import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { PageHeader, Section, Loading, ErrorBox } from "@/components/common/ui-bits";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PROJECT_TYPES } from "@/lib/format";

function TemplateDialog({ tpl, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [base, setBase] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [discard, setDiscard] = useState(false);
  useEffect(() => {
    if (tpl) { const v = { name: tpl.name || "", project_type: tpl.project_type || "", description: tpl.description || "", tasks: (tpl.tasks || [{ title: "" }]).map((t) => ({ visible_to_client: false, offset_days: 0, duration_days: 1, ...t })) }; setF(v); setBase(JSON.stringify(v)); setErr(""); }
  }, [tpl]);
  if (!tpl || !f) return null;
  const close = () => (JSON.stringify(f) !== base ? setDiscard(true) : onClose());
  const setTask = (i, p) => setF({ ...f, tasks: f.tasks.map((t, j) => (j === i ? { ...t, ...p } : t)) });
  const save = async () => {
    const tasks = f.tasks.filter((t) => t.title.trim());
    if (f.name.trim().length < 3 || !tasks.length) return setErr("Escriba un nombre (mín. 3 caracteres) y al menos una tarea.");
    setBusy(true);
    try {
      const body = { ...f, project_type: f.project_type || null, tasks };
      if (tpl.id) await api.put(`/templates/${tpl.id}`, body); else await api.post("/templates", body);
      toast.success("Plantilla guardada"); onSaved(); onClose();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="template-form">
          <DialogHeader><DialogTitle className="font-display">{tpl.id ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
            <DialogDescription>Día = inicio sugerido contado desde la fecha de inicio del proyecto (0 = mismo día). Dura = días hasta el vencimiento. Marque el ojo en las tareas que el cliente podrá ver.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="field-label">Nombre *</label><input className="field-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="template-name-input" /></div>
            <div><label className="field-label">Tipo de proyecto</label><select className="field-input" value={f.project_type} onChange={(e) => setF({ ...f, project_type: e.target.value })}><option value="">—</option>{PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          </div>
          <div className="space-y-2">
            {f.tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="num w-6 text-right text-xs text-slate-400">{i + 1}</span>
                <input className="field-input" value={t.title} onChange={(e) => setTask(i, { title: e.target.value })} data-testid={`template-task-${i}`} />
                <label className="flex items-center gap-1 text-[11px] text-slate-500" title="Día de inicio desde el comienzo del proyecto">Día<input type="number" min="0" className="field-input w-16 px-2" value={t.offset_days} onChange={(e) => setTask(i, { offset_days: Math.max(0, Number(e.target.value) || 0) })} data-testid={`template-task-offset-${i}`} /></label>
                <label className="flex items-center gap-1 text-[11px] text-slate-500" title="Duración en días">Dura<input type="number" min="1" className="field-input w-16 px-2" value={t.duration_days} onChange={(e) => setTask(i, { duration_days: Math.max(1, Number(e.target.value) || 1) })} data-testid={`template-task-duration-${i}`} /></label>
                <label className="flex items-center gap-1 text-xs text-slate-500" title="Visible para el cliente"><input type="checkbox" checked={t.visible_to_client} onChange={(e) => setTask(i, { visible_to_client: e.target.checked })} className="accent-blue-700" /><Eye className="h-3.5 w-3.5" /></label>
                <Button size="icon" variant="ghost" aria-label="Quitar" onClick={() => setF({ ...f, tasks: f.tasks.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setF({ ...f, tasks: [...f.tasks, { title: "", visible_to_client: false, offset_days: 0, duration_days: 1 }] })} data-testid="template-add-task"><Plus className="mr-1 h-3.5 w-3.5" />Tarea</Button>
          </div>
          {err && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
          <DialogFooter className="gap-2"><Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="template-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={discard} onOpenChange={setDiscard} destructive title="Hay cambios sin guardar" description="Se perderán los cambios." confirmLabel="Descartar" onConfirm={async () => { setDiscard(false); onClose(); }} />
    </>
  );
}

export default function Templates() {
  const { data, loading, error, reload } = useApi("/templates");
  const [editing, setEditing] = useState(null);
  return (
    <div>
      <PageHeader title="Plantillas de proyecto" actions={<Button onClick={() => setEditing({})} data-testid="new-template-button"><Plus className="mr-2 h-4 w-4" />Nueva plantilla</Button>} />
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : (
        <div className="grid gap-4 lg:grid-cols-2">
          {(data || []).map((t) => (
            <Section key={t.id} title={t.name} testid={`template-card-${t.id}`} actions={<Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`edit-template-${t.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>}>
              <p className="mb-3 text-xs text-slate-500">{t.project_type} · {t.tasks.length} tareas</p>
              <ol className="list-inside list-decimal columns-1 space-y-1 text-sm text-slate-700 sm:columns-2">
                {t.tasks.map((x, i) => <li key={i}>{x.title} <span className="text-xs text-slate-400">(día {x.offset_days ?? 0}, {x.duration_days ?? 1} d)</span>{x.visible_to_client && <Eye className="ml-1 inline h-3 w-3 text-blue-600" />}</li>)}
              </ol>
            </Section>
          ))}
        </div>
      )}
      <TemplateDialog tpl={editing} onClose={() => setEditing(null)} onSaved={reload} />
    </div>
  );
}
